/**
 * 时代选择页面
 * 选择商朝不同剧本，预览地图和势力
 * 现代极简风格设计
 */

import { useState, useMemo } from 'react'
import {
  SHANG_SCENARIOS,
  SHANG_FACTIONS,
  getFactionById,
  getCitiesByFaction,
  type Faction,
  type FactionRelation,
} from '../data/shangDynastyData'
import { getFactionRoyalArchive } from '../data/royalFamilyArchives'
import './EraSelectPage.css'

interface EraSelectPageProps {
  onBack: () => void
  onStartGame: (scenarioId: string, factionId: string) => void
}

export function EraSelectPage({ onBack, onStartGame }: EraSelectPageProps) {
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>(SHANG_SCENARIOS[2].id)
  const [selectedFactionId, setSelectedFactionId] = useState<string>('')

  const selectedScenario = useMemo(() => 
    SHANG_SCENARIOS.find(s => s.id === selectedScenarioId),
    [selectedScenarioId]
  )
  
  const selectedFaction = useMemo(() => 
    getFactionById(selectedFactionId),
    [selectedFactionId]
  )

  // 获取当前剧本的可玩势力
  const playableFactions = useMemo(() => {
    if (!selectedScenario) return []
    return selectedScenario.playableFactions
      .map(id => getFactionById(id))
      .filter((f): f is Faction => f !== undefined)
  }, [selectedScenario])

  // 获取势力的初始数据
  const factionStats = useMemo(() => {
    if (!selectedFaction) return null
    const cities = getCitiesByFaction(selectedFaction.id)
    const capital = cities.find(c => c.isStartingCapital)
    
    return {
      cities: cities.length,
      population: cities.reduce((sum, c) => sum + c.population, 0),
      capital: capital?.name || '未知',
      resources: cities.reduce((sum, c) => ({
        food: sum.food + c.resources.food,
        money: sum.money + c.resources.money,
        wood: sum.wood + c.resources.wood,
        metal: sum.metal + c.resources.metal,
      }), { food: 0, money: 0, wood: 0, metal: 0 }),
    }
  }, [selectedFaction])

  // 获取势力关系（根据所选剧本设置历史准确的外交关系）
  const factionRelations = useMemo(() => {
    if (!selectedFaction || !selectedScenario) return []
    
    const relations: Array<{ faction: Faction; relation: FactionRelation }> = []
    
    // 根据剧本设置历史准确的外交关系
    SHANG_FACTIONS.forEach(f => {
      if (f.id === selectedFaction.id) return
      
      let relation: FactionRelation = 'neutral'
      
      switch (selectedScenario.id) {
        case 'cheng-tang-revolution': // 成汤革命：商初建立，各方观望
          if (selectedFaction.id === 'shang') {
            if (f.type === 'nomadic') relation = 'hostile' // 边疆游牧民族威胁
            else if (f.type === 'vassal') relation = 'neutral' // 方国还在观望
            else relation = 'neutral'
          }
          break
          
        case 'pan-geng-move': // 盘庚迁殷：内外服制度已建立
          if (selectedFaction.id === 'shang') {
            if (f.type === 'vassal') relation = 'suzerain' // 臣服方国
            else if (f.type === 'nomadic' || f.type === 'hostile') relation = 'hostile' // 边患
            else relation = 'neutral'
          } else if (selectedFaction.type === 'vassal') {
            if (f.id === 'shang') relation = 'vassal'
            else if (f.type === 'nomadic') relation = 'hostile'
          }
          break
          
        case 'wu-ding-prosperity': // 武丁中兴：四处征伐，战争频繁
          if (selectedFaction.id === 'shang') {
            if (f.type === 'vassal') relation = 'suzerain'
            else if (f.id === 'guifang' || f.id === 'qiangfang' || f.id === 'renfang') relation = 'at_war' // 武丁征伐对象
            else if (f.type === 'nomadic' || f.type === 'hostile') relation = 'hostile'
          } else if (selectedFaction.id === 'guifang' || selectedFaction.id === 'qiangfang' || selectedFaction.id === 'renfang') {
            if (f.id === 'shang') relation = 'at_war' // 与商交战
            else if (f.type === 'nomadic' || f.type === 'hostile') relation = 'ally' // 游牧部落结盟
          }
          break
          
        case 'di-xin-disaster': // 帝辛之祸：周已崛起，关系紧张
          if (selectedFaction.id === 'shang') {
            if (f.id === 'zhoufang') relation = 'hostile' // 周已不服
            else if (f.type === 'vassal') relation = 'suzerain'
            else if (f.type === 'nomadic') relation = 'hostile'
          } else if (selectedFaction.id === 'zhoufang') {
            if (f.id === 'shang') relation = 'hostile' // 准备伐商
            else if (f.type === 'vassal') relation = 'ally' // 联络诸侯
            else if (f.type === 'nomadic') relation = 'neutral'
          }
          break
          
        case 'wu-wang-conquest': // 武王伐纣：牧野之战
          if (selectedFaction.id === 'zhoufang') {
            if (f.id === 'shang') relation = 'at_war' // 牧野之战
            else if (f.type === 'vassal') relation = 'ally' // 孟津之会的同盟
            else relation = 'neutral'
          } else if (selectedFaction.id === 'shang') {
            if (f.id === 'zhoufang') relation = 'at_war'
            else if (f.type === 'vassal') relation = 'neutral' // 部分方国观望
          }
          break
          
        default:
          // 默认逻辑
          if (selectedFaction.type === 'royal') {
            if (f.type === 'vassal') relation = 'suzerain'
            else if (f.type === 'hostile' || f.type === 'nomadic') relation = 'hostile'
          } else if (selectedFaction.type === 'vassal') {
            if (f.type === 'royal') relation = 'vassal'
          }
      }
      
      relations.push({ faction: f, relation })
    })
    
    // 按重要性排序：交战 > 敌对 > 同盟 > 宗主/臣服 > 中立
    const priority: Record<FactionRelation, number> = {
      'at_war': 5,
      'hostile': 4,
      'ally': 3,
      'suzerain': 2,
      'vassal': 2,
      'neutral': 1,
    }
    relations.sort((a, b) => priority[b.relation] - priority[a.relation])
    
    return relations.slice(0, 4) // 只显示前4个
  }, [selectedFaction, selectedScenario])

  // 处理剧本切换
  const handleScenarioChange = (scenarioId: string) => {
    setSelectedScenarioId(scenarioId)
    const scenario = SHANG_SCENARIOS.find(s => s.id === scenarioId)
    if (scenario) {
      setSelectedFactionId(scenario.playableFactions[0] || '')
    }
  }

  // 开始游戏
  const handleStartGame = () => {
    if (selectedScenarioId && selectedFactionId) {
      onStartGame(selectedScenarioId, selectedFactionId)
    }
  }

  // 渲染难度星星
  const renderDifficulty = (difficulty: number, size: 'sm' | 'md' = 'sm') => (
    <span className={`faction-difficulty ${size}`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < difficulty ? 'filled' : ''}>
          {i < difficulty ? '★' : '☆'}
        </span>
      ))}
    </span>
  )

  // 获取关系状态文本
  const getRelationLabel = (relation: FactionRelation): string => {
    const labels: Record<FactionRelation, string> = {
      suzerain: '宗主',
      vassal: '臣服',
      ally: '同盟',
      neutral: '中立',
      hostile: '敌对',
      at_war: '交战',
    }
    return labels[relation]
  }

  return (
    <div className="era-select-page">
      {/* 顶部标题 */}
      <header className="era-header">
        <h1 className="era-title">选择时代</h1>
      </header>

      {/* 剧本选择栏 */}
      <div className="scenario-bar">
        {SHANG_SCENARIOS.map(scenario => (
          <button
            key={scenario.id}
            className={`scenario-tab ${selectedScenarioId === scenario.id ? 'selected' : ''}`}
            onClick={() => handleScenarioChange(scenario.id)}
          >
            {scenario.name}
          </button>
        ))}
      </div>

      {/* 主内容区 */}
      <div className="era-content">
        {/* 剧本信息卡片 */}
        {selectedScenario && (
          <div className="scenario-info-card">
            <div className="scenario-info-main">
              <div className="scenario-info-header">
                <span className="scenario-info-name">{selectedScenario.name}</span>
                <span className="scenario-info-year">
                  {selectedScenario.startYear > 0 
                    ? `公元前 ${selectedScenario.startYear} 年` 
                    : `公元前 ${Math.abs(selectedScenario.startYear)} 年`}
                </span>
              </div>
              <p className="scenario-info-desc">{selectedScenario.description}</p>
            </div>
            <div className="scenario-info-meta">
              <span className="scenario-info-label">可选势力</span>
              <span className="scenario-info-factions">{selectedScenario.playableFactions.length}</span>
            </div>
          </div>
        )}

        {/* 势力选择区域 */}
        <section className="faction-selection-area">
          {/* 左侧势力列表 */}
          <aside className="faction-list-panel">
            <div className="faction-list-header">
              <h3 className="faction-list-title">选择势力</h3>
            </div>
            <div className="faction-name-list">
              {playableFactions.map(faction => {
                const isSelected = selectedFactionId === faction.id
                return (
                  <div
                    key={faction.id}
                    className={`faction-name-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedFactionId(faction.id)}
                  >
                    <div className="faction-name-left">
                      <span 
                        className="faction-color-indicator" 
                        style={{ backgroundColor: faction.color }}
                      />
                      <span className="faction-name-text">{faction.name}</span>
                    </div>
                    {renderDifficulty(faction.difficulty)}
                  </div>
                )
              })}
            </div>
          </aside>

          {/* 右侧势力详情 */}
          <main className="faction-detail-panel">
            {selectedFaction ? (
              <div className="faction-detail-content">
                {/* 详情头部 */}
                <div className="faction-detail-header">
                  <div className="faction-header-info">
                    <div className="faction-header-top">
                      <h2 
                        className="detail-faction-name"
                        style={{ color: selectedFaction.color }}
                      >
                        {selectedFaction.name}
                      </h2>
                      <span className="detail-faction-type">
                        {getFactionTypeLabel(selectedFaction.type)}
                      </span>
                    </div>
                    <div className="detail-difficulty">
                      <span>难度:</span>
                      <span className="detail-difficulty-stars">
                        {'★'.repeat(selectedFaction.difficulty)}{'☆'.repeat(5 - selectedFaction.difficulty)}
                      </span>
                    </div>
                    <div className="detail-traits">
                      {selectedFaction.traits.map(trait => (
                        <span key={trait} className="trait-badge">{trait}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 详情主体 */}
                <div className="faction-detail-body">
                  {/* 势力介绍 */}
                  <section className="detail-section">
                    <h4 className="detail-section-title">势力介绍</h4>
                    <p className="detail-description">{selectedFaction.description}</p>
                    {(() => {
                      const royalArchive = selectedScenario ? getFactionRoyalArchive(selectedScenario.id, selectedFaction.id) : null
                      const ruler = royalArchive?.members.find(m => m.identity === 'ruler')
                      return ruler ? (
                        <div className="ruler-info" style={{
                          marginTop: 'var(--es-space-4)',
                          padding: 'var(--es-space-3) var(--es-space-4)',
                          background: 'var(--es-bg-primary)',
                          border: '1px solid var(--es-border-subtle)',
                          borderLeft: `3px solid ${selectedFaction.color}`,
                          borderRadius: 'var(--es-radius-md)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--es-space-2)',
                        }}>
                          <span style={{ color: 'var(--es-text-muted)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>君主</span>
                          <span style={{ color: 'var(--es-text-primary)', fontSize: '15px', fontWeight: 600, fontFamily: '"Noto Serif SC", serif' }}>{ruler.name}</span>
                        </div>
                      ) : null
                    })()}
                  </section>

                  {/* 初始状态 */}
                  {factionStats && (
                    <section className="detail-section">
                      <h4 className="detail-section-title">初始状态</h4>
                      <div className="starting-stats">
                        <div className="stat-item">
                          <span className="stat-label">城邑</span>
                          <span className="stat-value">{factionStats.cities}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">人口</span>
                          <span className="stat-value">{(factionStats.population / 1000).toFixed(1)}k</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">都城</span>
                          <span className="stat-value">{factionStats.capital}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">粮食</span>
                          <span className="stat-value">{factionStats.resources.food.toLocaleString()}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">贝币</span>
                          <span className="stat-value">{factionStats.resources.money.toLocaleString()}</span>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* 外交关系 */}
                  {factionRelations.length > 0 && (
                    <section className="detail-section">
                      <h4 className="detail-section-title">外交关系</h4>
                      <div className="faction-relations">
                        {factionRelations.map(({ faction, relation }) => (
                          <div key={faction.id} className="relation-item">
                            <div className="relation-faction">
                              <span 
                                className="relation-dot" 
                                style={{ backgroundColor: faction.color }}
                              />
                              <span>{faction.name}</span>
                            </div>
                            <span className={`relation-status ${relation}`}>
                              {getRelationLabel(relation)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              </div>
            ) : (
              <div className="faction-detail-empty">
                <div className="faction-detail-empty-icon">⚔</div>
                <p className="faction-detail-empty-text">请选择势力查看详情</p>
              </div>
            )}
          </main>
        </section>
      </div>

      {/* 底部操作栏 */}
      <footer className="era-footer">
        <div className="footer-info">
          <span>华夏志</span>
          <span className="footer-divider" />
          <span>{selectedScenario?.name || '选择剧本'}</span>
          {selectedFaction && (
            <>
              <span className="footer-divider" />
              <span style={{ color: selectedFaction.color }}>{selectedFaction.name}</span>
            </>
          )}
        </div>
        <div className="footer-buttons">
          <button className="btn btn-secondary" onClick={onBack}>
            <span className="btn-icon">←</span>
            <span>返回</span>
          </button>
          <button
            className="btn btn-primary"
            onClick={handleStartGame}
            disabled={!selectedScenarioId || !selectedFactionId}
          >
            <span className="btn-icon">⚔</span>
            <span>开始霸业</span>
          </button>
        </div>
      </footer>
    </div>
  )
}

/** 获取势力类型标签 */
function getFactionTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    royal: '王室',
    vassal: '方国',
    hostile: '不服王化',
    independent: '独立',
    nomadic: '游牧',
  }
  return labels[type] || type
}
